// Typed permission registry — single source of truth for every
// permission code the frontend knows about. Generated from the
// Permission table (rows seeded across the migrations, starting
// in 0700_reference_data.sql).
// Keep this file in lockstep with the DB: adding a
// permission means adding a row here + granting it to roles in a
// new migration; removing one means deprecating here first so no
// page references a code that no longer exists.
//
// Two reasons this exists:
//   1. Typos. `user.requirePermission('PATIENT_VEIW')` silently
//      denies everyone. Strings in code bypass the type system;
//      `PERMS.PATIENT_VIEW` doesn't compile if mistyped.
//   2. Discoverability. A future engineer touching billing flows
//      can search for PERMS.CLAIM_SUBMIT and see every gate that
//      touches claim submission.
//
// Companion types (PermissionCode, PermissionScope) are consumed
// by <PermissionGate /> + the permission-explanation tooltip in
// R2; the PERMISSION_META map drives those tooltips.

// ── Scope ──────────────────────────────────────────────────────

/**
 * Scope dimension on every role-permission grant — `RolePermission.scope`
 * in the DB. Lets the frontend disable an action for
 * out-of-scope records instead of letting the user click and hit a
 * 403. Matches the `permission_scope` enum exactly.
 */
export type PermissionScope = 'own' | 'team' | 'unit' | 'facility' | 'all'

// ── Permission codes ──────────────────────────────────────────

/**
 * Every permission code the backend recognizes. Use `PERMS.X`
 * rather than a raw string — typos become compile errors, and
 * IDE autocomplete surfaces the full catalog.
 *
 * Grouped by the DB's `permission_category` column so a page
 * touching billing flows sees all the billing perms together.
 */
export const PERMS = {
  // ── Patient ─────────────────────────────────────────────────
  PATIENT_VIEW: 'PATIENT_VIEW',
  PATIENT_CREATE: 'PATIENT_CREATE',
  PATIENT_UPDATE: 'PATIENT_UPDATE',
  PATIENT_DELETE: 'PATIENT_DELETE',
  PATIENT_MERGE: 'PATIENT_MERGE',
  PATIENT_FULL_DEMOGRAPHICS_VIEW: 'PATIENT_FULL_DEMOGRAPHICS_VIEW',
  EMERGENCY_CONTACT_VIEW: 'EMERGENCY_CONTACT_VIEW',
  CROSS_FACILITY_VIEW: 'CROSS_FACILITY_VIEW',

  // Portal / caregiver access. PORTAL_OWN_DATA is patient-side;
  // PORTAL_PROXY_ACCESS is caregiver-side (PatientProxy table).
  PORTAL_OWN_DATA: 'PORTAL_OWN_DATA',
  PORTAL_PROXY_ACCESS: 'PORTAL_PROXY_ACCESS',

  // Sensitive-data overlays — ride on top of PATIENT_VIEW. A user
  // with PATIENT_VIEW but not SENSITIVE_BEHAVIORAL_VIEW sees the
  // general chart but behavioral-health material is redacted.
  RESTRICTED_VIEW: 'RESTRICTED_VIEW',
  SENSITIVE_BEHAVIORAL_VIEW: 'SENSITIVE_BEHAVIORAL_VIEW',
  SENSITIVE_HIV_VIEW: 'SENSITIVE_HIV_VIEW',
  SENSITIVE_GENETIC_VIEW: 'SENSITIVE_GENETIC_VIEW',
  SENSITIVE_ADOLESCENT_VIEW: 'SENSITIVE_ADOLESCENT_VIEW',
  SENSITIVE_SUBSTANCE_VIEW: 'SENSITIVE_SUBSTANCE_VIEW',
  SENSITIVE_VIP_VIEW: 'SENSITIVE_VIP_VIEW',

  // ── Encounter ───────────────────────────────────────────────
  ENCOUNTER_VIEW: 'ENCOUNTER_VIEW',
  ENCOUNTER_CREATE: 'ENCOUNTER_CREATE',
  ENCOUNTER_UPDATE: 'ENCOUNTER_UPDATE',
  ENCOUNTER_DISCHARGE: 'ENCOUNTER_DISCHARGE',
  TRANSFER_MANAGE: 'TRANSFER_MANAGE',
  ENCOUNTER_CLASS_CHANGE: 'ENCOUNTER_CLASS_CHANGE',
  ENCOUNTER_CLASS_CONCUR: 'ENCOUNTER_CLASS_CONCUR',

  // ── Orders ──────────────────────────────────────────────────
  ORDER_VIEW: 'ORDER_VIEW',
  ORDER_CREATE: 'ORDER_CREATE',
  ORDER_SIGN: 'ORDER_SIGN',
  ORDER_DISCONTINUE: 'ORDER_DISCONTINUE',
  ORDER_COSIGN: 'ORDER_COSIGN',
  // Granular cosign — regulatory-distinct workflows.
  ORDER_COSIGN_RESIDENT: 'ORDER_COSIGN_RESIDENT',
  ORDER_COSIGN_MIDLEVEL: 'ORDER_COSIGN_MIDLEVEL',
  VERBAL_ORDER_VERIFY: 'VERBAL_ORDER_VERIFY',
  RESTRAINT_ORDER: 'RESTRAINT_ORDER',
  DIET_ORDER_CREATE: 'DIET_ORDER_CREATE',
  BLOOD_ADMIN: 'BLOOD_ADMIN',

  // ── Medications ─────────────────────────────────────────────
  MED_VIEW: 'MED_VIEW',
  MED_PRESCRIBE: 'MED_PRESCRIBE',
  MED_ADMINISTER: 'MED_ADMINISTER',
  MED_VERIFY: 'MED_VERIFY',
  MED_RECONCILE: 'MED_RECONCILE',
  MED_DOUBLE_CHECK: 'MED_DOUBLE_CHECK',
  MED_VERIFY_OVERRIDE: 'MED_VERIFY_OVERRIDE',
  MED_CLARIFY_RESOLVE: 'MED_CLARIFY_RESOLVE',
  MED_DISPENSE: 'MED_DISPENSE',

  // ── Results ─────────────────────────────────────────────────
  RESULT_VIEW: 'RESULT_VIEW',
  RESULT_ENTER: 'RESULT_ENTER',
  RESULT_VERIFY: 'RESULT_VERIFY',
  RESULT_SIGN: 'RESULT_SIGN',
  LAB_CRITICAL_ACKNOWLEDGE: 'LAB_CRITICAL_ACKNOWLEDGE',

  // ── Documentation ───────────────────────────────────────────
  DOC_VIEW: 'DOC_VIEW',
  DOC_CREATE: 'DOC_CREATE',
  DOC_SIGN: 'DOC_SIGN',
  DOC_COSIGN: 'DOC_COSIGN',
  DOC_COSIGN_STUDENT: 'DOC_COSIGN_STUDENT',
  DOC_AMEND: 'DOC_AMEND',
  DISCHARGE_SUMMARY_SIGN: 'DISCHARGE_SUMMARY_SIGN',
  DISCHARGE_PLAN: 'DISCHARGE_PLAN',

  // ── Scheduling / Referral ───────────────────────────────────
  SCHEDULE_VIEW: 'SCHEDULE_VIEW',
  SCHEDULE_MANAGE: 'SCHEDULE_MANAGE',
  REFERRAL_VIEW: 'REFERRAL_VIEW',
  REFERRAL_CREATE: 'REFERRAL_CREATE',

  // ── Consent / Privacy ───────────────────────────────────────
  CONSENT_MANAGE: 'CONSENT_MANAGE',

  // ── Coverage / Delegation ───────────────────────────────────
  // Pre-scheduled covering-provider flow (ProviderCoverage table).
  // Distinct from BREAK_GLASS.
  COVERAGE_GRANT: 'COVERAGE_GRANT',
  COVERAGE_ADMIN: 'COVERAGE_ADMIN',

  // ── Billing / Financial ─────────────────────────────────────
  BILLING_VIEW: 'BILLING_VIEW',
  BILLING_CREATE: 'BILLING_CREATE', // umbrella — prefer the specific perms below
  BILLING_PAYMENT: 'BILLING_PAYMENT',
  CODING_ASSIGN: 'CODING_ASSIGN',
  CLAIM_SUBMIT: 'CLAIM_SUBMIT',
  PAYMENT_POST: 'PAYMENT_POST',
  DENIAL_WORK: 'DENIAL_WORK',

  // ── Research ────────────────────────────────────────────────
  RESEARCH_MANAGE: 'RESEARCH_MANAGE', // umbrella — prefer the specific perms below
  RESEARCH_IDENTIFY: 'RESEARCH_IDENTIFY',
  RESEARCH_ENROLL: 'RESEARCH_ENROLL',
  RESEARCH_PHI_ACCESS: 'RESEARCH_PHI_ACCESS',
  RESEARCH_ADMIN: 'RESEARCH_ADMIN',

  // ── Analytics / Infection control ───────────────────────────
  ANALYTICS_VIEW: 'ANALYTICS_VIEW',
  INFECTION_VIEW: 'INFECTION_VIEW',

  // ── Audit ───────────────────────────────────────────────────
  AUDIT_VIEW: 'AUDIT_VIEW',
  AUDIT_VIEW_PATIENT: 'AUDIT_VIEW_PATIENT',
  AUDIT_VIEW_USER: 'AUDIT_VIEW_USER',
  AUDIT_EXPORT: 'AUDIT_EXPORT',
  AUDIT_BREAK_GLASS_REVIEW: 'AUDIT_BREAK_GLASS_REVIEW',

  // ── Clinical decision support ───────────────────────────────
  CDS_INVOKE: 'CDS_INVOKE',

  // ── System administration ───────────────────────────────────
  SYS_ADMIN_ALL: 'SYS_ADMIN_ALL',
  USER_MANAGE: 'USER_MANAGE',
  LICENSING_MANAGE: 'LICENSING_MANAGE',
  DEPARTMENT_MANAGE: 'DEPARTMENT_MANAGE',
  LOCATION_MANAGE: 'LOCATION_MANAGE',
  BED_ASSIGN: 'BED_ASSIGN',
  SERVICE_TAXONOMY_MANAGE: 'SERVICE_TAXONOMY_MANAGE',
  NOTIFICATION_MANAGE: 'NOTIFICATION_MANAGE',
  BULK_EXPORT: 'BULK_EXPORT',
  INTERFACE_OPERATE: 'INTERFACE_OPERATE',
  SCAN_CROSS_FACILITY: 'SCAN_CROSS_FACILITY',
  TERMINOLOGY_VIEW: 'TERMINOLOGY_VIEW',

  // ── Emergency access ────────────────────────────────────────
  // Held by roles that CAN initiate break-glass; the actual access
  // expansion is a time-bounded workflow, not a standing grant.
  BREAK_GLASS: 'BREAK_GLASS',
} as const

/** Union type of every valid permission code. */
export type PermissionCode = (typeof PERMS)[keyof typeof PERMS]

// ── Categories ─────────────────────────────────────────────────

export type PermissionCategoryKey =
  | 'patient'
  | 'clinical'
  | 'financial'
  | 'system'

export const CATEGORY_LABELS: Record<PermissionCategoryKey, string> = {
  patient: 'Patient',
  clinical: 'Clinical',
  financial: 'Financial',
  system: 'System',
}

// ── Metadata ───────────────────────────────────────────────────

export interface PermissionMeta {
  code: PermissionCode
  /** Human-readable short label (sentence-case). */
  label: string
  category: PermissionCategoryKey
  /** One-line "what it lets you do" — drives tooltip in <PermissionGate />. */
  description: string
  /**
   * Which role codes typically hold this permission in a default
   * install. Informational — drives the "held by: Physician, Charge
   * Nurse" hint in permission-denied tooltips. Roles can still
   * grant or revoke per deployment.
   */
  typicalRoles?: readonly string[]
  /**
   * Granted to no role; the code stays so old references compile, but the
   * access matrix no longer offers it. Set together with the backend
   * `is_deprecated` flag — never alone.
   */
  deprecated?: boolean
}

/**
 * Metadata for every permission — keyed by code for O(1) lookup
 * from the authStore or from a <PermissionGate> tooltip.
 *
 * Kept here rather than fetched from the backend because:
 *   - It's small (under 3 KB gzipped), no network round-trip needed.
 *   - It's stable with releases (permission semantics don't change
 *     per user), so client-side caching is fine.
 *   - The UI needs synchronous access when rendering disabled
 *     buttons — a network fetch would flash the wrong state.
 */
export const PERMISSION_META: Record<PermissionCode, PermissionMeta> = {
  // Patient
  PATIENT_VIEW: {
    code: 'PATIENT_VIEW',
    label: 'View patients',
    category: 'patient',
    description: 'View patient demographics and chart.',
    typicalRoles: ['Physician', 'Nurse', 'Pharmacist', 'Lab Tech'],
  },
  PATIENT_CREATE: {
    code: 'PATIENT_CREATE',
    label: 'Register patients',
    category: 'patient',
    description: 'Register new patients.',
    typicalRoles: ['Registration Clerk', 'Nurse', 'ER Physician'],
  },
  PATIENT_UPDATE: {
    code: 'PATIENT_UPDATE',
    label: 'Update patients',
    category: 'patient',
    description: 'Update patient demographics + admin data.',
    typicalRoles: ['Registration Clerk', 'Nurse', 'Physician'],
  },
  PATIENT_DELETE: {
    code: 'PATIENT_DELETE',
    label: 'Delete patient records',
    category: 'patient',
    description: 'Delete patient records. Destructive; admin-only.',
    typicalRoles: ['System Administrator'],
  },
  PATIENT_MERGE: {
    code: 'PATIENT_MERGE',
    label: 'Merge duplicate patients',
    category: 'patient',
    description: 'Merge duplicate patient records discovered by EMPI.',
    typicalRoles: ['HIM', 'Medical Records'],
  },
  PATIENT_FULL_DEMOGRAPHICS_VIEW: {
    code: 'PATIENT_FULL_DEMOGRAPHICS_VIEW',
    label: 'View sensitive demographics',
    category: 'patient',
    description: 'View full SSN + home address. Most clinical users see masked.',
    typicalRoles: ['Registration Clerk', 'Billing', 'Physician'],
  },
  EMERGENCY_CONTACT_VIEW: {
    code: 'EMERGENCY_CONTACT_VIEW',
    label: 'View emergency contacts',
    category: 'patient',
    description: 'View patient emergency contact info without full chart access.',
    typicalRoles: ['Registration Clerk', 'Nurse', 'Social Worker'],
  },
  CROSS_FACILITY_VIEW: {
    code: 'CROSS_FACILITY_VIEW',
    label: 'Expand reads across facilities',
    category: 'patient',
    description: 'Expand a FHIR read across all facilities linked to the patient instead of one.',
    typicalRoles: ['HIM', 'System Administrator'],
  },
  PORTAL_OWN_DATA: {
    code: 'PORTAL_OWN_DATA',
    label: 'Portal — own data',
    category: 'patient',
    description: 'Access own patient portal data.',
    typicalRoles: ['Patient (Portal)'],
  },
  PORTAL_PROXY_ACCESS: {
    code: 'PORTAL_PROXY_ACCESS',
    label: 'Portal — proxy access',
    category: 'patient',
    description: 'Access a related patient (minor child, elderly parent) via proxy relationship.',
    typicalRoles: ['Caregiver/Guardian'],
  },
  RESTRICTED_VIEW: {
    code: 'RESTRICTED_VIEW',
    label: 'View restricted records',
    category: 'patient',
    description: 'View behavioral-health / HIV / VIP records. Legacy umbrella — prefer the specific SENSITIVE_* perms.',
  },
  SENSITIVE_BEHAVIORAL_VIEW: {
    code: 'SENSITIVE_BEHAVIORAL_VIEW',
    label: 'View behavioral health',
    category: 'patient',
    description: 'View behavioral-health / psychiatric records. Overlay on PATIENT_VIEW.',
    typicalRoles: ['Physician', 'Psychiatric Nurse'],
  },
  SENSITIVE_HIV_VIEW: {
    code: 'SENSITIVE_HIV_VIEW',
    label: 'View HIV status',
    category: 'patient',
    description: 'View HIV test results + related documentation.',
    typicalRoles: ['Physician', 'Specialty Nurse'],
  },
  SENSITIVE_GENETIC_VIEW: {
    code: 'SENSITIVE_GENETIC_VIEW',
    label: 'View genetic info',
    category: 'patient',
    description: 'View genetic test results (GINA-equivalent protection).',
    typicalRoles: ['Physician', 'Genetic Counselor'],
  },
  SENSITIVE_ADOLESCENT_VIEW: {
    code: 'SENSITIVE_ADOLESCENT_VIEW',
    label: 'View adolescent confidential',
    category: 'patient',
    description: 'View confidential adolescent (13–17) records.',
    typicalRoles: ['Physician', 'Adolescent Medicine'],
  },
  SENSITIVE_SUBSTANCE_VIEW: {
    code: 'SENSITIVE_SUBSTANCE_VIEW',
    label: 'View substance abuse',
    category: 'patient',
    description: 'View substance-abuse treatment records (42 CFR Part 2).',
    typicalRoles: ['Physician', 'Addiction Medicine'],
  },
  SENSITIVE_VIP_VIEW: {
    code: 'SENSITIVE_VIP_VIEW',
    label: 'View VIP / employee',
    category: 'patient',
    description: 'View VIP- or employee-flagged records. Chart-access reason required.',
    typicalRoles: ['Department Head', 'Quality Officer'],
  },

  // Encounter
  ENCOUNTER_VIEW: {
    code: 'ENCOUNTER_VIEW',
    label: 'View encounters',
    category: 'clinical',
    description: 'View encounter details.',
    typicalRoles: ['Physician', 'Nurse', 'Most clinical'],
  },
  ENCOUNTER_CREATE: {
    code: 'ENCOUNTER_CREATE',
    label: 'Create encounters',
    category: 'clinical',
    description: 'Open new encounters (admit, check-in, visit).',
    typicalRoles: ['Physician', 'Nurse', 'Registration Clerk'],
  },
  ENCOUNTER_UPDATE: {
    code: 'ENCOUNTER_UPDATE',
    label: 'Update encounters',
    category: 'clinical',
    description: 'Update encounter details (transfer, disposition).',
    typicalRoles: ['Physician', 'Nurse', 'Bed Manager'],
  },
  ENCOUNTER_DISCHARGE: {
    code: 'ENCOUNTER_DISCHARGE',
    label: 'Discharge patients',
    category: 'clinical',
    description: 'Authorize patient discharge.',
    typicalRoles: ['Physician', 'ER Physician', 'Charge Nurse'],
  },
  TRANSFER_MANAGE: {
    code: 'TRANSFER_MANAGE',
    label: 'Manage transfers',
    category: 'clinical',
    description: 'Manage patient transfers + bed assignments.',
    typicalRoles: ['Bed Manager', 'Charge Nurse'],
  },
  ENCOUNTER_CLASS_CHANGE: {
    code: 'ENCOUNTER_CLASS_CHANGE',
    label: 'Change encounter class',
    category: 'clinical',
    description: 'Place an admission order that changes an encounter class.',
    typicalRoles: ['Physician', 'Emergency Physician', 'Surgeon'],
  },
  ENCOUNTER_CLASS_CONCUR: {
    code: 'ENCOUNTER_CLASS_CONCUR',
    label: 'Concur with class downgrade',
    category: 'clinical',
    description: 'Second independent physician signature on a class downgrade.',
    typicalRoles: ['Physician', 'Emergency Physician', 'Surgeon'],
  },

  // Orders
  ORDER_VIEW: { code: 'ORDER_VIEW', label: 'View orders', category: 'clinical', description: 'View clinical orders.', typicalRoles: ['Physician', 'Nurse', 'Pharmacist'] },
  ORDER_CREATE: { code: 'ORDER_CREATE', label: 'Create orders', category: 'clinical', description: 'Place new clinical orders.', typicalRoles: ['Physician', 'NP/PA', 'Resident'] },
  ORDER_SIGN: { code: 'ORDER_SIGN', label: 'Sign orders', category: 'clinical', description: 'Sign + approve orders.', typicalRoles: ['Physician', 'NP/PA'] },
  ORDER_DISCONTINUE: { code: 'ORDER_DISCONTINUE', label: 'Discontinue orders', category: 'clinical', description: 'Discontinue active orders.', typicalRoles: ['Physician', 'NP/PA'] },
  ORDER_COSIGN: {
    code: 'ORDER_COSIGN',
    label: 'Cosign orders',
    category: 'clinical',
    description: 'Cosign orders from supervised providers. Umbrella — prefer the specific resident/midlevel perms below.',
    typicalRoles: ['Physician', 'Surgeon'],
  },
  ORDER_COSIGN_RESIDENT: {
    code: 'ORDER_COSIGN_RESIDENT',
    label: 'Cosign resident orders',
    category: 'clinical',
    description: 'DEPRECATED (1120): the queue and sign action take the ORDER_COSIGN umbrella; trainee identity does not exist to scope this.',
    typicalRoles: [],
    deprecated: true,
  },
  ORDER_COSIGN_MIDLEVEL: {
    code: 'ORDER_COSIGN_MIDLEVEL',
    label: 'Cosign midlevel orders',
    category: 'clinical',
    description: 'DEPRECATED (1120): the queue and sign action take the ORDER_COSIGN umbrella.',
    typicalRoles: [],
    deprecated: true,
  },
  VERBAL_ORDER_VERIFY: {
    code: 'VERBAL_ORDER_VERIFY',
    label: 'Verify verbal orders',
    category: 'clinical',
    description: 'Authenticate verbal orders within the 24h JC window.',
    typicalRoles: ['Physician', 'NP/PA'],
  },
  RESTRAINT_ORDER: {
    code: 'RESTRAINT_ORDER',
    label: 'Order restraints',
    category: 'clinical',
    description: 'Document + order patient restraints (regulatory).',
    typicalRoles: ['Physician', 'ER Physician'],
  },
  DIET_ORDER_CREATE: {
    code: 'DIET_ORDER_CREATE',
    label: 'Order diets',
    category: 'clinical',
    description: 'Create + modify nutritional / diet orders.',
    typicalRoles: ['Physician', 'NP/PA', 'Dietitian'],
  },
  BLOOD_ADMIN: {
    code: 'BLOOD_ADMIN',
    label: 'Administer blood',
    category: 'clinical',
    description: 'Administer blood transfusions + blood products.',
    typicalRoles: ['Physician', 'Nurse'],
  },

  // Medications
  MED_VIEW: { code: 'MED_VIEW', label: 'View medications', category: 'clinical', description: 'View medication orders.', typicalRoles: ['Most clinical'] },
  MED_PRESCRIBE: { code: 'MED_PRESCRIBE', label: 'Prescribe medications', category: 'clinical', description: 'Order medications.', typicalRoles: ['Physician', 'NP/PA'] },
  MED_VERIFY_OVERRIDE: { code: 'MED_VERIFY_OVERRIDE', label: 'Override pharmacist verification', category: 'clinical', description: 'Advance a medication to active without pharmacist verification, where pharmacy is not staffed around the clock. Records a reason code and justification; does NOT satisfy the high-alert pharmacy gate.', typicalRoles: ['Physician', 'Charge Nurse'] },
  MED_CLARIFY_RESOLVE: { code: 'MED_CLARIFY_RESOLVE', label: 'Resolve medication clarification', category: 'clinical', description: 'Resolve a pharmacy-versus-prescriber discrepancy as a pharmacist, under a collaborative practice agreement or P&T protocol. Granted to no role by default. A prescriber needs no such permission -- amending an order is prescribing.', typicalRoles: [] },
  MED_DISPENSE: { code: 'MED_DISPENSE', label: 'Dispense medications', category: 'clinical', description: 'Record that a physical medication product was prepared and handed over.', typicalRoles: ['Pharmacist', 'Pharmacy Technician'] },
  MED_ADMINISTER: { code: 'MED_ADMINISTER', label: 'Administer medications', category: 'clinical', description: 'Administer medications (MAR).', typicalRoles: ['Nurse'] },
  MED_VERIFY: { code: 'MED_VERIFY', label: 'Verify medications', category: 'clinical', description: 'Pharmacist verification.', typicalRoles: ['Pharmacist'] },
  MED_RECONCILE: { code: 'MED_RECONCILE', label: 'Reconcile medications', category: 'clinical', description: 'Perform medication reconciliation at transitions.', typicalRoles: ['Physician', 'Pharmacist', 'Nurse'] },
  MED_DOUBLE_CHECK: { code: 'MED_DOUBLE_CHECK', label: 'Double-check high-risk meds', category: 'clinical', description: 'Independent double-check for high-alert medications.', typicalRoles: ['Nurse', 'Pharmacist'] },

  // Results
  RESULT_VIEW: { code: 'RESULT_VIEW', label: 'View results', category: 'clinical', description: 'View lab + imaging results.', typicalRoles: ['Most clinical'] },
  RESULT_ENTER: { code: 'RESULT_ENTER', label: 'Enter results', category: 'clinical', description: 'Enter lab results.', typicalRoles: ['Lab Tech'] },
  RESULT_VERIFY: { code: 'RESULT_VERIFY', label: 'Verify results', category: 'clinical', description: 'Verify and sign results.', typicalRoles: ['Lab Tech', 'Pathologist'] },
  RESULT_SIGN: { code: 'RESULT_SIGN', label: 'Sign results', category: 'clinical', description: 'Sign + finalize clinical results.', typicalRoles: ['Pathologist', 'Radiologist'] },
  LAB_CRITICAL_ACKNOWLEDGE: {
    code: 'LAB_CRITICAL_ACKNOWLEDGE',
    label: 'Acknowledge critical labs',
    category: 'clinical',
    description: 'Acknowledge critical-value lab notifications (JC NPSG.02.03.01).',
    typicalRoles: ['Physician', 'Nurse', 'NP/PA'],
  },

  // Documentation
  DOC_VIEW: { code: 'DOC_VIEW', label: 'View notes', category: 'clinical', description: 'View clinical notes.', typicalRoles: ['Most clinical'] },
  DOC_CREATE: { code: 'DOC_CREATE', label: 'Create notes', category: 'clinical', description: 'Create clinical notes.', typicalRoles: ['Physician', 'Nurse', 'NP/PA'] },
  DOC_SIGN: { code: 'DOC_SIGN', label: 'Sign notes', category: 'clinical', description: 'Sign clinical notes.', typicalRoles: ['Physician', 'NP/PA'] },
  DOC_COSIGN: {
    code: 'DOC_COSIGN',
    label: 'Cosign notes',
    category: 'clinical',
    description: 'Cosign clinical notes. Umbrella — student-note cosign has its own perm.',
    typicalRoles: ['Physician', 'Resident'],
  },
  DOC_COSIGN_STUDENT: {
    code: 'DOC_COSIGN_STUDENT',
    label: 'Cosign student notes',
    category: 'clinical',
    description: 'Cosign notes authored by medical / nursing students.',
    typicalRoles: ['Physician', 'Resident'],
  },
  DOC_AMEND: {
    code: 'DOC_AMEND',
    label: 'Amend signed notes',
    category: 'clinical',
    description: 'Amend previously-signed clinical notes.',
    typicalRoles: ['Physician', 'NP/PA'],
  },
  DISCHARGE_SUMMARY_SIGN: {
    code: 'DISCHARGE_SUMMARY_SIGN',
    label: 'Sign discharge summary',
    category: 'clinical',
    description: 'Sign + finalize discharge summaries.',
    typicalRoles: ['Physician', 'NP/PA'],
  },
  DISCHARGE_PLAN: {
    code: 'DISCHARGE_PLAN',
    label: 'Discharge planning',
    category: 'clinical',
    description: 'Create + manage discharge planning documentation.',
    typicalRoles: ['Social Worker', 'Case Manager', 'Nurse'],
  },

  // Scheduling / Referral
  SCHEDULE_VIEW: { code: 'SCHEDULE_VIEW', label: 'View schedules', category: 'clinical', description: 'View appointment schedules.', typicalRoles: ['Most clinical'] },
  SCHEDULE_MANAGE: { code: 'SCHEDULE_MANAGE', label: 'Manage schedules', category: 'clinical', description: 'Create + modify schedule templates.', typicalRoles: ['Registration Clerk', 'Department Head'] },
  REFERRAL_VIEW: { code: 'REFERRAL_VIEW', label: 'View referrals', category: 'clinical', description: 'View incoming + outgoing referrals.', typicalRoles: ['Physician', 'NP/PA'] },
  REFERRAL_CREATE: { code: 'REFERRAL_CREATE', label: 'Create referrals', category: 'clinical', description: 'Create + send patient referrals.', typicalRoles: ['Physician', 'NP/PA'] },

  // Consent
  CONSENT_MANAGE: { code: 'CONSENT_MANAGE', label: 'Manage consent', category: 'clinical', description: 'Create + manage patient consent forms.', typicalRoles: ['Registration Clerk', 'Nurse'] },

  // Coverage
  COVERAGE_GRANT: {
    code: 'COVERAGE_GRANT',
    label: 'Grant coverage',
    category: 'clinical',
    description: 'Hand off your panel to a colleague for a time window.',
    typicalRoles: ['Physician', 'NP/PA', 'Surgeon'],
  },
  COVERAGE_ADMIN: {
    code: 'COVERAGE_ADMIN',
    label: 'Admin coverage',
    category: 'clinical',
    description: 'Create / revoke coverage entries on behalf of other providers.',
    typicalRoles: ['Department Head', 'Charge Nurse'],
  },

  // Billing
  BILLING_VIEW: { code: 'BILLING_VIEW', label: 'View billing', category: 'financial', description: 'View charges + billing.', typicalRoles: ['Billing', 'Physician', 'Department Head'] },
  BILLING_CREATE: {
    code: 'BILLING_CREATE',
    label: 'Create charges',
    category: 'financial',
    description: 'Create charges. Umbrella — prefer the specific perms (CODING_ASSIGN, CLAIM_SUBMIT, PAYMENT_POST).',
    typicalRoles: ['Billing'],
  },
  BILLING_PAYMENT: { code: 'BILLING_PAYMENT', label: 'Process payments', category: 'financial', description: 'Process payments.', typicalRoles: ['Billing', 'Cashier'] },
  CODING_ASSIGN: {
    code: 'CODING_ASSIGN',
    label: 'Assign diagnosis codes',
    category: 'financial',
    description: 'Assign ICD-10 / CPT / HCPCS codes.',
    typicalRoles: ['Medical Coder', 'HIM'],
  },
  CLAIM_SUBMIT: {
    code: 'CLAIM_SUBMIT',
    label: 'Submit claims',
    category: 'financial',
    description: 'Submit claims to payers (EDI).',
    typicalRoles: ['Billing'],
  },
  PAYMENT_POST: {
    code: 'PAYMENT_POST',
    label: 'Post payments',
    category: 'financial',
    description: 'Post patient payments + payer remittances.',
    typicalRoles: ['Billing', 'Cashier'],
  },
  DENIAL_WORK: {
    code: 'DENIAL_WORK',
    label: 'Work denied claims',
    category: 'financial',
    description: 'Review + appeal denied claims.',
    typicalRoles: ['Billing', 'Insurance Coordinator'],
  },

  // Research
  RESEARCH_MANAGE: {
    code: 'RESEARCH_MANAGE',
    label: 'Manage research',
    category: 'clinical',
    description: 'Legacy umbrella — prefer the specific RESEARCH_* perms.',
  },
  RESEARCH_IDENTIFY: {
    code: 'RESEARCH_IDENTIFY',
    label: 'Identify research patients',
    category: 'clinical',
    description: 'Screen cohorts without PHI access ("honest broker" role).',
    typicalRoles: ['Research Coordinator'],
  },
  RESEARCH_ENROLL: {
    code: 'RESEARCH_ENROLL',
    label: 'Enroll research patients',
    category: 'clinical',
    description: 'Obtain informed consent + enroll patients in studies.',
    typicalRoles: ['Research Coordinator'],
  },
  RESEARCH_PHI_ACCESS: {
    code: 'RESEARCH_PHI_ACCESS',
    label: 'Access research PHI',
    category: 'clinical',
    description: 'Access enrolled patients PHI under IRB protocol.',
    typicalRoles: ['Research Coordinator', 'Principal Investigator'],
  },
  RESEARCH_ADMIN: {
    code: 'RESEARCH_ADMIN',
    label: 'Admin research studies',
    category: 'clinical',
    description: 'Set up + administer studies.',
    typicalRoles: ['Research Coordinator'],
  },

  // Analytics / Infection
  ANALYTICS_VIEW: { code: 'ANALYTICS_VIEW', label: 'View analytics', category: 'clinical', description: 'View analytics dashboards + reports.', typicalRoles: ['Department Head', 'Quality Officer'] },
  INFECTION_VIEW: { code: 'INFECTION_VIEW', label: 'View infection data', category: 'clinical', description: 'View infection control data + antibiograms.', typicalRoles: ['Infection Control'] },

  // Audit
  AUDIT_VIEW: { code: 'AUDIT_VIEW', label: 'View audit log', category: 'system', description: 'View the system audit trail.', typicalRoles: ['Quality Officer', 'System Administrator'] },
  AUDIT_VIEW_PATIENT: {
    code: 'AUDIT_VIEW_PATIENT',
    label: 'Audit — patient access log',
    category: 'system',
    description: 'See who accessed a specific patient chart + why.',
    typicalRoles: ['Quality Officer', 'System Administrator'],
  },
  AUDIT_VIEW_USER: {
    code: 'AUDIT_VIEW_USER',
    label: 'Audit — user activity log',
    category: 'system',
    description: 'See what a specific user did.',
    typicalRoles: ['Quality Officer', 'System Administrator'],
  },
  AUDIT_EXPORT: {
    code: 'AUDIT_EXPORT',
    label: 'Export audit logs',
    category: 'system',
    description: 'Export audit logs to CSV/JSON.',
    typicalRoles: ['Quality Officer', 'System Administrator'],
  },
  AUDIT_BREAK_GLASS_REVIEW: {
    code: 'AUDIT_BREAK_GLASS_REVIEW',
    label: 'Review break-glass events',
    category: 'system',
    description: 'Review + sign off on break-glass emergency access events.',
    typicalRoles: ['Quality Officer', 'Compliance'],
  },

  // Clinical decision support
  CDS_INVOKE: { code: 'CDS_INVOKE', label: 'Invoke CDS services', category: 'clinical', description: 'Invoke CDS Hooks clinical decision support services.', typicalRoles: ['Physician', 'NP/PA', 'Resident'] },

  // System administration
  SYS_ADMIN_ALL: { code: 'SYS_ADMIN_ALL', label: 'System administration', category: 'system', description: 'Full system administration.', typicalRoles: ['System Administrator'] },
  USER_MANAGE: { code: 'USER_MANAGE', label: 'Manage users', category: 'system', description: 'Manage users + roles.', typicalRoles: ['System Administrator', 'HR'] },
  LICENSING_MANAGE: { code: 'LICENSING_MANAGE', label: 'Manage licenses', category: 'system', description: 'Install and inspect license keys without full system administration.', typicalRoles: ['System Administrator'] },
  DEPARTMENT_MANAGE: { code: 'DEPARTMENT_MANAGE', label: 'Manage departments', category: 'system', description: 'Create / edit / retire departments.', typicalRoles: ['System Administrator'] },
  LOCATION_MANAGE: { code: 'LOCATION_MANAGE', label: 'Manage locations', category: 'system', description: 'Create / edit / retire physical locations.', typicalRoles: ['System Administrator'] },
  BED_ASSIGN: {
    code: 'BED_ASSIGN',
    label: 'Assign beds',
    category: 'system',
    description: 'Toggle bed operational status (HL7 0116).',
    typicalRoles: ['Charge Nurse', 'Bed Manager'],
  },
  SERVICE_TAXONOMY_MANAGE: { code: 'SERVICE_TAXONOMY_MANAGE', label: 'Manage service taxonomy', category: 'system', description: 'Manage service lines + clinical services.', typicalRoles: ['System Administrator'] },
  INTERFACE_OPERATE: { code: 'INTERFACE_OPERATE', label: 'Operate HL7 interfaces', category: 'system', description: 'Retry, skip, abandon, or resend HL7 messages; view decrypted raw HL7 payloads in the message log.', typicalRoles: ['System Administrator', 'Integration Engineer'] },
  SCAN_CROSS_FACILITY: { code: 'SCAN_CROSS_FACILITY', label: 'Scan deduplication across facilities', category: 'system', description: 'Run patient deduplication scans across facility boundaries. Must be held explicitly; granted to no role by default.', typicalRoles: [] },
  TERMINOLOGY_VIEW: { code: 'TERMINOLOGY_VIEW', label: 'View terminology services', category: 'system', description: 'Read FHIR terminology services ($lookup, $expand, $validate-code). Granted to every staff category.' },
  NOTIFICATION_MANAGE: { code: 'NOTIFICATION_MANAGE', label: 'Manage notifications', category: 'system', description: 'Manage notification rules + escalation.', typicalRoles: ['System Administrator'] },
  BULK_EXPORT: {
    code: 'BULK_EXPORT',
    label: 'Bulk export',
    category: 'system',
    description: 'Export bulk patient data.',
    typicalRoles: ['HIM', 'System Administrator'],
  },

  // Emergency
  BREAK_GLASS: {
    code: 'BREAK_GLASS',
    label: 'Emergency access (break-glass)',
    category: 'system',
    description: 'Initiate time-bounded emergency access to restricted records. Logged + reviewed.',
    typicalRoles: ['ER Physician', 'Charge Nurse'],
  },
}

/** Compile-time assertion: every code in PERMS has a meta entry. */
type _MetaCompleteness = {
  [K in PermissionCode]: (typeof PERMISSION_META)[K]
}
// If a code is ever added to PERMS without a matching meta, this
// type expression fails to resolve (unused variable is fine in
// TS strict mode because of the self-referencing cross-check).
const _metaCheck: _MetaCompleteness = PERMISSION_META
void _metaCheck
